import {
    FC,
    useCallback,
    useEffect,
    useState,
} from 'react';

import { message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '../../../hooks';
import { PhoneIdentity, RecipientRoles } from '../../../../types/types';
import { RecipientToDisplay } from '../../../../stores/SharedUsersStore/interfaces';

import RecipientPhone from './RecipientPhone';
import RecipientPassword from './RecipientPassword';
import RecipientChip from './RecipientChip';
import RecipientRoleSelect, { SharedItemType } from '../../RecipientRoleSelect';
import { ChangePhone } from '../../EditPhoneInput';
import { phoneNameSpace } from '../../PhoneComp';
import MissingPhoneInput from '@/components/Common/MissingDetails/MissingPhoneInput';
import MissingPasswordInput from '@/components/Common/MissingDetails/MissingPasswordInput';

import styles from './RecipientsListItem.module.scss';

const passwordNameSpace = 'sharingBlock.recipientsList';

interface RecipientListItemProps {
    showPhones: boolean;
    showPasswords?: boolean;
    hasAccessRoleToggle?: boolean;
    useChipInputs?: boolean;
    sharedItemType?: SharedItemType;
    recipient: RecipientToDisplay;
}

const RecipientsListItem: FC<RecipientListItemProps> = ({
    showPhones,
    showPasswords = false,
    recipient,
    sharedItemType,
    hasAccessRoleToggle = true,
    useChipInputs = false,
}) => {
    const {
        sharedUsersStore:
        {
            partialUpdateRecipient,
            removeRecipient,
            getRecipientPasswordInfo,
            setRecipientPassword,
        },
        usersPhonesStore: {
            changePhoneNumber,
            getUserPhone,
            fetchUserPhone,
        },
    } = useStores();

    const {
        email,
        role,
        type,
        id,
    } = recipient;

    const { t } = useTranslation();

    const [maskedPhone, setMaskedPhone] = useState<PhoneIdentity>(null);
    const [isPhoneChanging, setIsPhoneChanging] = useState<boolean>(false);
    const [isPhoneSearching, setIsPhoneSearching] = useState<boolean>(false);

    const fetchPhone = async (): Promise<void> => {
        if (type !== 'group') {
            setIsPhoneSearching(true);
            await fetchUserPhone(email);
            setIsPhoneSearching(false);
        }
    };

    useEffect(() => {
        fetchPhone();
    }, [email]);

    const { phonesVector: { currentPhone } } = getUserPhone(email);

    // Get password info for this recipient
    const passwordInfo = getRecipientPasswordInfo(email);

    const changeAccess = useCallback((key: RecipientRoles): void => {
        partialUpdateRecipient(email, { role: key });
    }, []);

    const onPhoneChange: ChangePhone = useCallback(async (phoneNumber) => {
        setIsPhoneChanging(true);
        const result = await changePhoneNumber({ email, phone: phoneNumber.phone, prefix: phoneNumber.prefix });
        if (result.success) {
            setMaskedPhone(phoneNumber);
            message.success(t(`${phoneNameSpace}.successfullyNumberChange`));
        } else {
            const errorMsg = result.errorMessage || t(`${phoneNameSpace}.invalidPhone`);
            message.error(errorMsg);
        }
        setIsPhoneChanging(false);
    }, [email]);

    const onPasswordChange = useCallback(async (password: string): Promise<void> => {
        const result = await setRecipientPassword(email, password);
        if (result.success) {
            message.success(t(`${passwordNameSpace}.passwordUpdated`));
        } else {
            const errorMsg = result.errorMessage || t(`${passwordNameSpace}.passwordUpdateError`);
            message.error(errorMsg);
        }
    }, [email, setRecipientPassword, t]);

    const onRemoveClick = useCallback(() => {
        removeRecipient(id);
    }, [id]);

    const isGroup = type !== 'user';

    const phone = maskedPhone || currentPhone;

    const onChipPhoneSubmit = useCallback(async (phoneNumber: PhoneIdentity) => {
        onPhoneChange(phoneNumber);
    }, [onPhoneChange]);

    const [isChipPasswordSaving, setIsChipPasswordSaving] = useState(false);
    const onChipPasswordSave = useCallback(async (password: string): Promise<void> => {
        setIsChipPasswordSaving(true);
        await onPasswordChange(password);
        setIsChipPasswordSaving(false);
    }, [onPasswordChange]);

    const hasPhoneData = !!(phone?.phone);
    const [isPhoneEditing, setIsPhoneEditing] = useState(false);
    const showPhoneInline = showPhones && useChipInputs && !isGroup && hasPhoneData && !isPhoneEditing;
    const showPhoneBelow = showPhones && useChipInputs && !isGroup && (!hasPhoneData || isPhoneEditing);

    return (
        <li key={email}>
            <div className={styles['recipient-row']}>
                <RecipientChip
                    content={email}
                    onClose={onRemoveClick}
                    isGroup={isGroup}
                    id={id}
                />
                {showPhones && useChipInputs && (isPhoneSearching || isPhoneChanging) && (
                    <span className={styles['chip-loader']}><LoadingOutlined spin /></span>
                )}
                {showPhoneInline && !isPhoneSearching && !isPhoneChanging && (
                    <MissingPhoneInput
                        phone={phone.phone}
                        prefix={phone.prefix}
                        onSubmit={onChipPhoneSubmit}
                        onEditingChange={setIsPhoneEditing}
                    />
                )}
                {hasAccessRoleToggle && (
                    <div className={styles['file-access']} data-testid="sharingDrawer_permissions_ui">
                        <RecipientRoleSelect
                            value={role}
                            sharedItemType={sharedItemType}
                            onChange={changeAccess}
                        />
                    </div>
                )}
            </div>
            {showPhoneBelow && !isPhoneSearching && !isPhoneChanging && (
                <MissingPhoneInput
                    phone={phone?.phone}
                    prefix={phone?.prefix}
                    onSubmit={onChipPhoneSubmit}
                    onEditingChange={setIsPhoneEditing}
                    initialEditing={isPhoneEditing}
                />
            )}
            {showPhones && !useChipInputs && (
                <RecipientPhone
                    prefix={phone?.prefix}
                    phone={phone?.phone}
                    isSearching={isPhoneSearching || isPhoneChanging}
                    changePhone={onPhoneChange}
                    groupId={isGroup ? id : null}
                />
            )}
            {showPasswords && !isGroup && useChipInputs && passwordInfo?.isChecking && (
                <span className={styles['chip-loader']}><LoadingOutlined spin /></span>
            )}
            {showPasswords && !isGroup && useChipInputs && !passwordInfo?.isChecking && (
                <MissingPasswordInput
                    maskedPassword={passwordInfo?.maskedPassword}
                    onSave={onChipPasswordSave}
                    isLoading={isChipPasswordSaving}
                />
            )}
            {showPasswords && !isGroup && !useChipInputs && (
                <RecipientPassword
                    maskedPassword={passwordInfo.maskedPassword}
                    isSearching={passwordInfo.isChecking}
                    changePassword={onPasswordChange}
                />
            )}
        </li>
    );
};

export default observer(RecipientsListItem);
